import { Injectable, Type } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { DeviceInfoService } from '@herrenknecht-connected/angular-shared-utils-core';
import { InputWithSelectModalComponent } from './input-with-select.component';
import { InputWithSelectData } from './input-with-select.type';
import { ModuleSpecificHelpData, ModuleSpecificHelpModalComponent } from './module-specific-help';
import { ReleaseNotesData, ReleaseNotesModalComponent } from './release-notes.component';
import { ReportViewerData, ReportViewerModalComponent } from './report-viewer.component';
import { SimpleConfirmModalComponent, SimpleConfirmModalData } from './simple-confirm.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { TranslateService } from '@ngx-translate/core';
import { SnackbarData, SnackBarHtmlContentComponent } from './snack-bar-html-content.component';

export const ModalSizeConfig = {
  XXS: { width: '250px', height: '200px' },
  XS: { width: '350px', height: '312px' },
  S: { width: '532px', height: '468px' },
  M: { width: '716px', height: '624px' },
  L: { width: '1080px', height: '819px' },
  XL: { width: '1446px', height: '897px' },
};

const DEFAULT_SIMPLE_CONFIRM_CONFIG: MatDialogConfig = {
  ...ModalSizeConfig.XS,
  panelClass: 'connected-modal',
  autoFocus: false,
  hasBackdrop: true,
};

const DEFAULT_INPUT_WITH_SELECT_CONFIG: MatDialogConfig = {
  ...ModalSizeConfig.XS,
  panelClass: 'connected-modal',
  autoFocus: false,
  hasBackdrop: true,
};

const DEFAULT_RELEASE_NOTE_CONFIG: MatDialogConfig = {
  ...ModalSizeConfig.M,
  panelClass: 'connected-modal',
  autoFocus: false,
  hasBackdrop: true,
};

const DEFAULT_MODULE_SPECIFIC_HELP_CONFIG: MatDialogConfig = {
  ...ModalSizeConfig.M,
  panelClass: 'connected-modal',
  autoFocus: false,
  hasBackdrop: true,
};

const DEFAULT_REPORT_VIEWER_CONFIG: MatDialogConfig = {
  ...ModalSizeConfig.XL,
  height: 'calc(100vh - 24px)',
  panelClass: ['connected-modal'],
  autoFocus: false,
  hasBackdrop: true,
};

@Injectable({
  providedIn: 'root',
})
export class UiModalsService {
  private readonly _snackbarDuration = 5000;

  constructor(
    private _dialog: MatDialog,
    private _snackBar: MatSnackBar,
    private _translate: TranslateService,
    private _deviceInfo: DeviceInfoService,
  ) {
    if (_deviceInfo.isMobile) {
      (DEFAULT_REPORT_VIEWER_CONFIG.panelClass as string[]).push('no-padding-modal');
    }
  }

  public alert(message: string) {
    return this._snackBar.open(message, this._translate.instant('common.dismiss'), {
      panelClass: 'alert-snackbar',
    });
  }

  public info(message: string) {
    return this._snackBar.openFromComponent(SnackBarHtmlContentComponent, {
      data: {
        message: message,
        action: this._translate.instant('common.ok'),
      } as SnackbarData,
      duration: this._snackbarDuration,
    });
  }

  public createSimpleConfirmModal(data: SimpleConfirmModalData, dialogConfig: MatDialogConfig = {}) {
    return this._dialog.open(SimpleConfirmModalComponent, this.assignConfig(data, dialogConfig, DEFAULT_SIMPLE_CONFIRM_CONFIG));
  }

  public createModuleSpecificHelpModal(data: ModuleSpecificHelpData, dialogConfig: MatDialogConfig = {}) {
    return this._dialog.open(ModuleSpecificHelpModalComponent, this.assignConfig(data, dialogConfig, DEFAULT_MODULE_SPECIFIC_HELP_CONFIG));
  }

  public createInputWithSelectModal(data: InputWithSelectData, dialogConfig: MatDialogConfig = {}) {
    return this._dialog.open(InputWithSelectModalComponent, this.assignConfig(data, dialogConfig, DEFAULT_INPUT_WITH_SELECT_CONFIG));
  }

  public createReportViewerModal(data: ReportViewerData, dialogConfig: MatDialogConfig = {}) {
    return this._dialog.open(ReportViewerModalComponent, this.assignConfig(data, dialogConfig, DEFAULT_REPORT_VIEWER_CONFIG));
  }

  public createReleaseNotesModal(data: ReleaseNotesData, dialogConfig: MatDialogConfig = {}) {
    return this._dialog.open(ReleaseNotesModalComponent, this.assignConfig(data, dialogConfig, DEFAULT_RELEASE_NOTE_CONFIG));
  }

  public createModuleHelpBasicVideoModal(videoUrl: string, dialogConfig: MatDialogConfig = {}) {
    return this.createModuleSpecificHelpModal(
      {
        videoTutorialUrl: videoUrl,
        showBasicVideoHelpComponent: true,
      },
      dialogConfig,
    );
  }

  public createModuleHelpCustomModal(component: Type<unknown>, dialogConfig: MatDialogConfig = {}) {
    return this.createModuleSpecificHelpModal(
      {
        contentComponent: component,
        videoTutorialUrl: '',
        showBasicVideoHelpComponent: false,
      },
      dialogConfig,
    );
  }

  public createSmallModal<DATA>(clazz: Type<unknown>, data: DATA, dialogConfig: MatDialogConfig = {}) {
    return this._dialog.open(clazz, this.assignConfig(data, dialogConfig, { ...ModalSizeConfig.XS }));
  }

  private assignConfig(data: unknown, dialogConfig: MatDialogConfig, defaultConfig: MatDialogConfig) {
    const config = { ...defaultConfig, ...dialogConfig };
    config.data = data;
    return config;
  }
}
